import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RUNTIME_CONFIGURATION, serviceRootOf } from '@processpuzzle/util';
import { map, Observable } from 'rxjs';
import { Signup, SignupRequest } from './signup';
import { SignupMapper } from './signup.mapper';

/**
 * `POST /signups` and `GET /signups/{orgKey}` on the gateway.
 *
 * The submit answers **202**, not 201: the organization is committed but its realm, its
 * administrator's invitation and its seeded data follow, so the seed is not usable yet. That is why
 * there is a status read at all and why the confirmation page polls it — the response to the submit
 * is a promise rather than a finished thing.
 *
 * Neither call is retried automatically, and the submit especially must not be. A timeout on it is
 * genuinely ambiguous: the tenant may have been committed, and a blind retry would either
 * double-provision or answer 409 for a key the customer had in fact just claimed. The organization
 * key is what makes the situation recoverable — a second submit of the same key answers 409 rather
 * than creating a second tenant — but that is a decision for a person, not for an interceptor.
 */
@Injectable({ providedIn: 'root' })
export class SignupService {
  private readonly httpClient = inject(HttpClient);
  private readonly mapper = inject(SignupMapper);
  private readonly baseUrl = serviceRootOf(inject(RUNTIME_CONFIGURATION), 'SUBSCRIPTION_SERVICE_ROOT');

  submit(request: SignupRequest): Observable<Signup> {
    return this.httpClient.post<unknown>(`${this.baseUrl}/signups`, request).pipe(map((dto) => this.mapper.fromDto(dto)));
  }

  findStatus(orgKey: string): Observable<Signup> {
    return this.httpClient.get<unknown>(`${this.baseUrl}/signups/${orgKey}`).pipe(map((dto) => this.mapper.fromDto(dto)));
  }
}
